import React, { useCallback, useEffect, useRef, useState } from 'react';
import Anchor from './Anchor/Anchor';
import Toolbar from '../components/Toolbar/Toolbar';
import { Slate, Editable, ReactEditor } from 'slate-react';
import type { Element, NodeEntry, Descendant, Editor } from 'slate';
import {
  StartControllerScope,
  ApplicationView,
  useController,
} from '@aha-app/mvc';
import Selections from '../collaboration/components/Selections';
import { type ToolbarOptions } from '../types/aha-editor';
import Leaf from '../components/Leaf';
import { default as ElementComponent } from '../components/Element'; // eslint-disable-line import/no-named-default
import { type ToolbarTool } from './Toolbar/types';
import SelectionToolbar from './SelectionToolbar/SelectionToolbar';
import AhaEditorV2Controller, {
  type AhaEditorV2ControllerProps,
} from '../controllers/AhaEditorV2Controller';
import Placeholder from './Placeholder';
import EditorLayoutComponents from './EditorLayoutComponents';
import type {
  EditableProps,
  RenderChunkProps,
  RenderPlaceholderProps,
} from 'slate-react/dist/components/editable';
import styles from './AhaEditorV2.module.css';
import classNames from 'classnames';
import useAhaEditorFocused from './useAhaEditorFocused';
import AhaEditorUtils from '../AhaEditorUtils/AhaEditorUtils';

export interface AhaEditorV2Props extends AhaEditorV2ControllerProps {
  onChange?: (value: Descendant[], editor: Editor) => void;
  toolbar?: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  compactToolbar?: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  ultraCompactToolbar?: {
    left?: ToolbarTool[];
    right?: ToolbarTool[];
  };
  toolbarOptions?: ToolbarOptions;
}

const renderElement: EditableProps['renderElement'] = (props) => (
  <ElementComponent {...props} />
);
const renderLeaf: EditableProps['renderLeaf'] = (props) => <Leaf {...props} />;

// Add content-visibility: auto to lowest chunks for rendering optimization
// https://docs.slatejs.org/walkthroughs/09-performance#optimizing-dom-painting
const renderChunk = ({ attributes, lowest, children }: RenderChunkProps) => (
  <div
    {...attributes}
    style={lowest ? { contentVisibility: 'auto' } : undefined}
  >
    {children}
  </div>
);

const AhaEditorV2 = ({
  behaviors = {},
  fetchUserInfo,
  toolbarOptions = { offset: 0 },
  value,
  onChange,
  toolbar,
  compactToolbar,
  ultraCompactToolbar,
}: AhaEditorV2Props) => {
  const controller = useController(AhaEditorV2Controller);
  const { readOnly, interactive, editorRenderId, minimumLines } =
    controller.state;
  const { ahaEditor, collaborativeDocument } = controller;
  const { editor } = ahaEditor;
  const {
    showBorder = false,
    hiddenToolbar = false,
    inline = false,
  } = behaviors;
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const slateFocused = ReactEditor.isFocused(editor);
  const focused = useAhaEditorFocused() && interactive;
  const [floatingToolbarHeight, setFloatingToolbarHeight] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  editorRenderId; // Use the value to trigger a re-render when it changes.

  const handleChange = (value: Descendant[]) => {
    if (onChange) {
      onChange(value, editor);
    }
  };

  const decorate = (entry: NodeEntry<Element>) => controller.decorate(entry);

  const renderPlaceholder: EditableProps['renderPlaceholder'] = useCallback(
    (props: RenderPlaceholderProps) => (
      <Placeholder
        editor={editor}
        placeholderChildren={behaviors.placeholderChildren}
        {...props}
      />
    ),
    [editor]
  );

  useEffect(() => {
    if (editorContainerRef.current) {
      controller.actionSetContainerElement(editorContainerRef.current);

      const unmountHandlers = controller.actionRunOnMountHandlers();
      return () => {
        unmountHandlers.forEach((handler) => handler());
      };
    }
  }, []);

  useEffect(() => {
    const preventSaveHandler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && focused) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', preventSaveHandler);

    return () => {
      window.removeEventListener('keydown', preventSaveHandler);
    };
  }, [focused]);

  useEffect(() => {
    controller.actionFadeInToolbar();
  }, [controller]);

  const editorStyles: React.CSSProperties = {};

  // Slate provides a minHeight for us automatically if we have dynamic placeholder content.
  // This means we can't define a minHeight and get Slate's dynamic minHeight at the same time.
  if (minimumLines) {
    editorStyles[
      '--aha-editor-v2--body-min-height'
    ] = `calc((var(--aha-editor--user-font-size) + var(--aha-editor--block-margin)) * ${minimumLines} + 8px)`;
  }

  const fullScreen = controller.state.pluginState.isFullScreen ?? false;

  const bodyClassName = classNames('aha-editor-v2__body', styles.body, {
    [styles.bodyFocused]: slateFocused,
  });

  const isEmpty = AhaEditorUtils.Editor.isEmpty(editor);

  return (
    <div
      className={classNames({
        [styles.wrapper]: true,
        [styles.fullScreen]: fullScreen,
        [styles.notFullScreen]: !fullScreen,
      })}
      style={editorStyles}
    >
      <EditorLayoutComponents ahaEditor={ahaEditor} location="left" />

      {/* @ts-expect-error TS complaining about onFocus/onBlur event types */}
      <div
        ref={editorContainerRef}
        tabIndex={!interactive ? undefined : 0}
        className={classNames(styles.container, {
          // Border visibility options: always, never, or only when focused
          [styles.showBorderAlways]: showBorder === true,
          [styles.showBorderNever]: showBorder === false,
          [styles.showBorderFocused]: showBorder === 'focused',

          // Focused + blurred states
          [styles.focused]: focused,
          [styles.blurred]: interactive && !focused,

          // Inline mode positions the toolbar appear above the editor content
          [styles.inline]: inline,

          // Hides the toolbar until focused
          [styles.hiddenToolbar]: hiddenToolbar,

          // Is the editor read-only (no editing allowed)
          [styles.readOnly]: !interactive,

          // Whether the editor is interactive: can a user click into it, see the toolbars, etc.
          [styles.interactive]: interactive,

          // Can the content be edited
          [styles.editable]: !readOnly && interactive,

          // Whether the editor has content or not
          [styles.hasContent]: !isEmpty,
          [styles.isEmpty]: isEmpty,
        })}
        data-editor-editable={interactive}
        {...controller.editorContainerEventHandlers()}
      >
        <EditorLayoutComponents ahaEditor={ahaEditor} location="above" />

        <Slate
          editor={editor}
          initialValue={value.children}
          onChange={handleChange}
        >
          <div
            className={classNames('aha-editor-v2', styles.editor)}
            style={{
              '--aha-editor-v2--toolbar-height': focused
                ? `${floatingToolbarHeight}px`
                : `0px`,
            }}
          >
            {toolbar && interactive && (
              <Anchor
                toolbarOffset={toolbarOptions.offset}
                fullScreen={fullScreen}
                focused={focused}
              >
                <Toolbar
                  toolbarTools={toolbar}
                  readOnly={!interactive}
                  compactToolbarTools={compactToolbar}
                  ultraCompactToolbarTools={ultraCompactToolbar}
                  compactAtWidth={toolbarOptions?.compactAtWidth}
                  ultraCompactAtWidth={toolbarOptions?.ultraCompactAtWidth}
                  floatingToolbarHeight={floatingToolbarHeight}
                  setFloatingToolbarHeight={setFloatingToolbarHeight}
                />
              </Anchor>
            )}

            <div
              className={classNames('aha-editor-v2__content', styles.content)}
            >
              <EditorLayoutComponents
                ahaEditor={ahaEditor}
                location="above_content"
              />

              <div>
                {/* @ts-expect-error ts doesn't agree with the event handler signatures */}
                <Editable
                  style={{ outline: 'none' }}
                  className={bodyClassName}
                  renderLeaf={renderLeaf}
                  renderElement={renderElement}
                  renderChunk={renderChunk}
                  decorate={decorate}
                  readOnly={readOnly}
                  renderPlaceholder={renderPlaceholder}
                  onDOMBeforeInput={controller.onDOMBeforeInput}
                  {...controller.eventHandlers()}
                  {...controller.state.editableProps}
                />
              </div>

              {controller.behaviors.selectionToolbars?.map(
                (selectionToolbar, i) => (
                  <SelectionToolbar
                    key={i}
                    ahaEditor={ahaEditor}
                    tools={selectionToolbar.tools}
                    position={selectionToolbar.position}
                  />
                )
              )}

              <EditorLayoutComponents
                ahaEditor={ahaEditor}
                location="below_content"
              />
            </div>

            {controller.hasSelections && collaborativeDocument && (
              <Selections
                editor={editor}
                fetchUserInfo={fetchUserInfo}
                collaborativeDocument={collaborativeDocument}
              />
            )}
          </div>
        </Slate>

        <EditorLayoutComponents ahaEditor={ahaEditor} location="below" />
      </div>

      <EditorLayoutComponents ahaEditor={ahaEditor} location="right" />
    </div>
  );
};

export default StartControllerScope(
  AhaEditorV2Controller,
  ApplicationView(AhaEditorV2)
);
